import * as Enumerable from 'linq';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { exhaustMap, filter, lastValueFrom, Observable, switchMap, tap } from 'rxjs';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { AccountEntity, ActivityEntityName, Allocation, ContractorRecommendedResourcesRequest, DialogType, SystemWorkOrderStatus } from '@jms/model';
import { ConfirmService } from '@jms/ui/ui.confirm';
import { ShellDialogService } from '@jms/ui/ui.shell.dialog.service';
import { ObservableInput } from '@jms/utils/util.redux';
import { StateComponent } from '@jms/utils/util.state.component';
import { TemplateHelperService } from '@jms/utils/util.templates';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { APPOINTMENT_RESOURCES_SERVICE, APPOINTMENT_SERVICE, AppointmentContext, AppointmentEditorContext, AppointmentResourcesService, AppointmentsService } from '../../../common';
import { AppointmentEditorComponent } from '../../../editor';
import { ToastService } from '@workbuddy/ui/services';

interface AppointmentDashboardComponentVM {
  context: AppointmentContext;
  appointments: Allocation[];
  filteredAppointments: Allocation[];
  selected: Allocation;
  search: string;
}

const initialState: AppointmentDashboardComponentVM = {
  context: null,
  appointments: [],
  filteredAppointments: [],
  selected: null,
  search: null,
};


@UntilDestroy()
@Component({
  selector: 'appointment-dashboard',
  templateUrl: './appointment.dashboard.component.html',
  styleUrls: ['./appointment.dashboard.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TemplateHelperService,
    ShellDialogService,
  ],
  standalone: false
})
export class AppointmentDashboardComponent extends StateComponent<AppointmentDashboardComponentVM> {

  @ObservableInput() @Input('context') public context$: Observable<AppointmentContext>;

  @Output() public refresh = new EventEmitter();

  public readonly DialogType = DialogType;
  public readonly Entity = ActivityEntityName;

  // Statuses where the WorkOrder is terminal — no new appointments allowed (MAI-1305).
  private static readonly closedStageStatuses: SystemWorkOrderStatus[] = [
    SystemWorkOrderStatus.Finalised,
    SystemWorkOrderStatus.Closed,
    SystemWorkOrderStatus.Canceled,
  ];

  public get canAdd(): boolean {
    const wo = this.vm?.context?.workOrder;
    if (this.vm?.context?.readonly || wo && AppointmentDashboardComponent.closedStageStatuses.includes(wo.status?.systemStatus)) {
      return false;
    }
    return this.appointmentsService.canAdd();
  }

  private activeDialog: DynamicDialogRef;

  // Guards against rapid repeat clicks stacking multiple editor dialogs; set on entry, released when the dialog closes.
  private opening = false;

  constructor(
    @Inject(APPOINTMENT_SERVICE) private appointmentsService: AppointmentsService,
    @Inject(APPOINTMENT_RESOURCES_SERVICE) private resourcesService: AppointmentResourcesService,
    private dialogs: ShellDialogService,
    private confirmService: ConfirmService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
  ) {
    super(initialState);

    this.context$.pipe(
      untilDestroyed(this),
      filter(context => !!context),
      exhaustMap(context => {

        context.timezone = context.timezone ?? 'Australia/Sydney';

        this.setStateDirect({ context, search: null });

        return this.appointmentsService.getAll(context.params);
      }),

      tap(appointments => {

        this.setStateDirect({ appointments: appointments });

        this.onSearch(this.vm.search);

      })

    ).subscribe();

    this.appointmentsService.error$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((error) => {
        this.toastService.error(error);
      });

  }


  public onSearch(search: string) {

    const result = Enumerable.from(this.vm.appointments)
      .where(appointment => {

        if (!search) return true;

        const reg = new RegExp(search, 'i');

        const searchValue = `${appointment?.type ?? ''} ${appointment?.note ?? ''} ${appointment?.createdBy?.name ?? ''} ${appointment?.resource?.name ?? ''}`;

        return (searchValue.toLowerCase().search(reg) !== -1);

      })
      .distinct(x => x._id)
      .toArray();

    this.setStateDirect({ filteredAppointments: result, search: search });

    this.cdr.markForCheck();

  }

  public async openEditor(item?: Allocation) {

    if (this.opening || this.vm?.context?.readonly) return;
    this.opening = true;

    try {

      const contractor: AccountEntity = this.vm.context.workOrder?.technician as AccountEntity ?? undefined;
      const resourceRequest: ContractorRecommendedResourcesRequest = { parentId: contractor?._id, stageId: this.vm.context.workOrder?._id };

      const resources = this.vm.context.entity === ActivityEntityName.Opportunity
        ? await lastValueFrom(this.resourcesService.getResources())
        : await lastValueFrom(this.resourcesService.getRecommendedResources?.(resourceRequest) ?? this.resourcesService.getResources(contractor?._id))


      const appointment = item?._id
        ? await lastValueFrom(this.appointmentsService.get(item?._id))
        : undefined;

      const { timezone, settings, entity, params } = this.vm.context;

      const editorContext: AppointmentEditorContext = {
        appointment,
        resources: resources as any[],
        timezone,
        settings,
        source: this.appointmentsService.source,
        entity,
        contractor,
        resourceRequest,
      };

      this.activeDialog = this.dialogs.open(AppointmentEditorComponent, {
        showHeader: false,
        height: '90%',
        width: '1800px',

        modal: true,
        dismissableMask: false,
        closeOnEscape: false,

        style: {
          maxWidth: '90%',
          maxHeight: '100%'
        },

        contentStyle: {
          height: '100%',
          padding: 0
        },

        data: {
          context: editorContext,
        },

        // styleClass: '-dialog'

      } as DynamicDialogConfig);


      this.activeDialog.onClose
        .pipe(
          untilDestroyed(this),
          tap(() => this.opening = false),
          filter(x => !!x),
          switchMap((x: Allocation) => {

            const allocation: Allocation = x;

            const saved$: Observable<Allocation | Allocation[]> = x?._id
              ? this.appointmentsService.update(allocation)
              : this.appointmentsService.create(allocation, this.vm.context);

            return saved$;
          }),
          switchMap(_ =>
            this.appointmentsService.getAll(params)
              .pipe(
                tap(appointments => {
                  this.reload(appointments);
                })
              )
          )
        )
        .subscribe();

    } catch (error) {
      this.opening = false;
      throw error;
    }
  }


  public async onDelete(item: Allocation) {

    if (this.vm?.context?.readonly) return;
    const confirm = await this.confirmService.confirm('Delete Appointment', 'Are you sure you wish to delete this appointment?', 'danger');

    if (!confirm) return;

    const result = await lastValueFrom(this.appointmentsService.delete(item?._id));

    if (result?.success) {
      const appointments = Enumerable.from(this.vm.appointments)
        .where(x => x._id !== result?._id)
        .toArray();

      this.reload(appointments);
    }

  }

  private reload(appointments: Allocation[]): void {
    this.setStateDirect({ appointments });
    this.onSearch(this.vm.search);
    this.refresh.emit();
  }


}
